import { useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import type { PermissionKey } from '@/lib/permissions';
import { pickActiveClinic, useAuthStore } from '@/stores/authStore';
import type { LoginResponse, RegisterPayload, RegisterResponse } from '@/types/api';

export function useAuth() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const memberships = useAuthStore((s) => s.memberships);
  const setSession = useAuthStore((s) => s.setSession);
  const logout = useAuthStore((s) => s.logout);

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await api.post<LoginResponse>('/api/auth/login', { email, password });
      setSession(data);
      return data;
    },
    [setSession],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      const data = await api.post<RegisterResponse>('/api/auth/register', payload);
      setSession(data);
      return data;
    },
    [setSession],
  );

  const activeClinic = pickActiveClinic(memberships);

  // Derived once per render from the membership. There is no fallback to a
  // field on `user`: the old code had one for synthetic test fixtures, and the
  // result was that real users — who never carry it — silently rendered as
  // staff. Permissions live on the membership, full stop.
  const permissions = useMemo<ReadonlySet<PermissionKey>>(
    () => new Set(activeClinic?.permissions ?? []),
    [activeClinic],
  );

  return {
    user,
    token,
    memberships,
    activeClinic,
    permissions,
    isAuthenticated: Boolean(token && user),
    login,
    register,
    logout,
  };
}
